import { toZonedTime } from 'date-fns-tz';
import { useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import toast from 'react-hot-toast';
import {
  addRoundTimelineInsertion,
  appendStreamedTimelineText,
  type TimelineTextInsertion,
  type TimelineWaitMarker,
  timelineInsertionsFromTurnState,
} from '@/components/case-partner/components/assistantTurnTimeline';
import type { QuickReplyQuestion } from '@/components/case-partner/quick-replies';
import {
  normalizeQuickReplyQuestions,
  quickReplyQuestionsFromMetadata,
} from '@/components/case-partner/quick-replies';
import type { ActiveTemplateChoices } from '@/components/case-partner/template-choices';
import {
  normalizeTemplateChoices,
  templateChoicesFromMetadata,
} from '@/components/case-partner/template-choices';
import { useCaseContext } from '@/components/contexts/CaseContext';
import { apiErrorMessage, backendClient } from '@/lib/api-client';
import { authFetch } from '@/lib/auth/session';
import { normalizeUiChanges, notifyUiChanged, uiChangeKey } from '@/lib/uiChangeBus';
import type { BackendDocumentSelection, DocumentSelection } from '@/types';
import { getUserTimezone, isoToZonedDate } from '@/utils/date-utils';
import {
  applyTurnEvent,
  closeOpenWaitMarkers,
  emptyTurnReducerState,
  positiveDurationMs,
  reasoningDurationMetadata,
  sealLiveTurnPresentation,
  sealUnfinishedSteps,
  type TurnReducerState,
  thinkingStepsFromSnapshot,
  turnReducerFromSnapshot,
} from './casePartnerTurnReducer';

function uiChangesFromTurn(state: { ui_changes?: { kind: string; id: string }[] }) {
  return state.ui_changes ?? [];
}

function notifyTurnUiChanges(state: { ui_changes?: { kind: string; id: string }[] }) {
  for (const change of uiChangesFromTurn(state)) {
    notifyUiChanged(change.kind, change.id);
  }
}


export type { QuickReplyOption, QuickReplyQuestion } from '@/components/case-partner/quick-replies';
export {
  thinkingStepKey,
  thinkingStepsFromSnapshot,
} from './casePartnerTurnReducer';

export interface ThinkingStep {
  type: 'reasoning' | 'tool';
  data: ReasoningStep | ToolCall;
  round_num?: number;
  summary_index?: number;
}

export interface Message {
  id?: number;
  messageIds?: number[];
  content: string;
  role: 'user' | 'assistant' | 'system';
  timestamp: Date;
  message_metadata?: any;
  draftCardsDefaultExpanded?: boolean;
  document_guids?: DocumentSelection[];
  thinkingSteps?: ThinkingStep[];
  timelineInsertions?: TimelineTextInsertion[];
  waitMarkers?: TimelineWaitMarker[];
}

export interface ReasoningStep {
  text: string;
  isComplete?: boolean;
  summary_index?: number;
  round_num?: number;  // Track which round this reasoning step belongs to
  // How long the round this step belongs to spent thinking. Carried by the last
  // summary of the round only, so a chip can add up its steps.
  duration_ms?: number;
}

/**
 * The thought a stored round holds, and how long that round spent on it.
 *
 * The round records its thinking time once, for the stretch as a whole, so the
 * last summary carries it — the same step the live reducer puts it on. Putting
 * it on every summary would have a chip adding up one stretch several times.
 */
export const reasoningStepsFromStoredRound = (
  round: { summary?: unknown; duration_ms?: unknown } | null | undefined,
  fallbackRoundNum?: number,
): ThinkingStep[] => {
  const summary = Array.isArray(round?.summary) ? round.summary : [];
  const durationMs = typeof round?.duration_ms === 'number' && round.duration_ms > 0
    ? round.duration_ms
    : undefined;
  return summary.map((step: ReasoningStep, index: number) => {
    const roundNum = step.round_num ?? fallbackRoundNum;
    const summaryIndex = step.summary_index ?? index;
    const data = durationMs && index === summary.length - 1
      ? { ...step, duration_ms: durationMs }
      : { ...step };
    return {
      type: 'reasoning' as const,
      data,
      round_num: roundNum,
      summary_index: summaryIndex,
    };
  });
};

export interface ToolCall {
  call_id?: string;  // Unique identifier for this tool call
  round_num?: number;  // Track which round this tool call is from
  displayInfo?: string;  // Friendly display text from backend (e.g., "Searching: class certification")
  function: {
    name: string;
    arguments: string;
    output?: string;
  };
  status?: 'pending' | 'in_progress' | 'completed' | 'failed' | 'stopped';
  family?: string;
  objective?: string;
  // Steps a background agent produced under this call. Same shape as the
  // lead's own steps, so they render through the same code.
  children?: AgentStep[];
}

export interface AgentStep {
  type: 'reasoning' | 'tool' | 'text';
  round_num?: number;
  summary_index?: number;
  data: Record<string, unknown>;
}


export interface ChatSession {
  guid: string;
  title: string;
  messages: Message[];
  createdAt: Date;
  updatedAt?: Date | null;
  isTemporary?: boolean;
  activeGenerationId?: string | null;
  activeGenerationStatus?: string | null;
  activeGenerationStartedAt?: string | null;
  searchSnippet?: string;
  searchMessageId?: number;
}

export function isActiveGenerationStatus(status: string | null | undefined): boolean {
  return status === 'generating' || status === 'queued' || status === 'stopping';
}

/** In-progress work the lawyer should still see as running. `stopping` is not. */
export function isRunningGenerationStatus(status: string | null | undefined): boolean {
  return status === 'generating' || status === 'queued';
}

export function isCurrentChatTurnActive(
  isStreamingCurrentChat: boolean,
  activeGenerationStatus?: string | null,
): boolean {
  return isStreamingCurrentChat || isActiveGenerationStatus(activeGenerationStatus);
}

export function isStoppingGenerationStatus(status: string | null | undefined): boolean {
  return status === 'stopping';
}

export function chatHasVisibleRunningTurn(
  chat: { guid: string; activeGenerationStatus?: string | null },
  streamingChatGuid?: string | null,
): boolean {
  if (isStoppingGenerationStatus(chat.activeGenerationStatus)) return false;
  return streamingChatGuid === chat.guid || isRunningGenerationStatus(chat.activeGenerationStatus);
}

/** Reconnect should look stopped: the worker may still be winding down. */
export function generationResumeShouldLookStopped(
  status: string | null | undefined,
  hasPendingStop: boolean,
): boolean {
  return isStoppingGenerationStatus(status) || hasPendingStop;
}

const STOP_LATCH_PREFIX = 'case-partner:stop:';

export interface PendingStopLatch {
  chatGuid: string;
  lastEventId: string | null;
}

function stopLatchStorage(): Storage | null {
  try {
    if (typeof sessionStorage === 'undefined') return null;
    return sessionStorage;
  } catch {
    return null;
  }
}

export function stopLatchKey(generationId: string): string {
  return `${STOP_LATCH_PREFIX}${generationId}`;
}

export function writeStopLatch(input: {
  generationId: string;
  chatGuid: string;
  lastEventId: string | null;
}): void {
  const storage = stopLatchStorage();
  if (!storage || !input.generationId || !input.chatGuid) return;
  try {
    storage.setItem(stopLatchKey(input.generationId), JSON.stringify({
      chatGuid: input.chatGuid,
      lastEventId: input.lastEventId,
    } satisfies PendingStopLatch));
  } catch {
    // Private mode / quota: the in-memory stop still runs; a refresh may miss it.
  }
}

export function readStopLatch(generationId: string | null | undefined): PendingStopLatch | null {
  const storage = stopLatchStorage();
  if (!storage || !generationId) return null;
  try {
    const raw = storage.getItem(stopLatchKey(generationId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PendingStopLatch>;
    if (typeof parsed.chatGuid !== 'string' || !parsed.chatGuid) return null;
    return {
      chatGuid: parsed.chatGuid,
      lastEventId: typeof parsed.lastEventId === 'string' ? parsed.lastEventId : null,
    };
  } catch {
    return null;
  }
}

export function clearStopLatch(generationId: string | null | undefined): void {
  const storage = stopLatchStorage();
  if (!storage || !generationId) return;
  try {
    storage.removeItem(stopLatchKey(generationId));
  } catch {
    // Ignore storage failures; a stale latch is retried as an idempotent stop.
  }
}

export function streamEventTerminalKind(data: {
  type?: string;
  is_complete?: boolean;
  error?: unknown;
}): 'error' | 'stopped' | 'complete' | null {
  if (data.type === 'error' || (typeof data.error === 'string' && data.error)) {
    return 'error';
  }
  if (data.type === 'stopped') {
    return 'stopped';
  }
  if (data.type === 'complete' || data.is_complete) {
    return 'complete';
  }
  return null;
}

export function shouldRetryDroppedGenerationStream(input: {
  streamError: string | null;
  receivedCompleteEvent: boolean;
  receivedStoppedEvent: boolean;
  detachRequested: boolean;
  isActiveStream: boolean;
  stopRequested: boolean;
}): boolean {
  return (
    input.isActiveStream &&
    !input.detachRequested &&
    !input.streamError &&
    !input.receivedCompleteEvent &&
    !input.receivedStoppedEvent &&
    // Stopping aborts the stream on purpose. Reconnecting would reopen a turn
    // the lawyer has already been shown the end of.
    !input.stopRequested
  );
}

export const STREAM_RECONNECT_BACKOFF_MS = [250, 750, 2000, 4000, 8000] as const;

export function streamReconnectDelayMs(consecutiveFailures: number): number | null {
  if (consecutiveFailures < 0) {
    return null;
  }
  const lastIndex = STREAM_RECONNECT_BACKOFF_MS.length - 1;
  return STREAM_RECONNECT_BACKOFF_MS[Math.min(consecutiveFailures, lastIndex)];
}

export function isRetryableGenerationStreamFailure(error: unknown): boolean {
  if (error && typeof error === 'object' && 'name' in error && error.name === 'AbortError') {
    return false;
  }
  const status = (
    error && typeof error === 'object' && 'status' in error && typeof error.status === 'number'
      ? error.status
      : null
  );
  if (status !== null && status >= 400 && status < 500 && status !== 408 && status !== 429) {
    return false;
  }
  return true;
}

/** Non-retryable failures must stop auto-resume; retryable ones must not. */
export function shouldLatchFinishedGenerationOnStreamFailure(error: unknown): boolean {
  return !isRetryableGenerationStreamFailure(error);
}

function abortError(): Error {
  const error = new Error('Aborted');
  error.name = 'AbortError';
  return error;
}

export function waitForAbortableDelay(ms: number, signal?: AbortSignal | null): Promise<void> {
  if (signal?.aborted) {
    return Promise.reject(abortError());
  }
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timeoutId);
      reject(abortError());
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/** Local-append dedup only. Resume/attach uses ``active_generation_status``. */
export function transcriptHasAssistantForGeneration(
  messages: Message[],
  generationId?: string | null,
): boolean {
  if (!generationId) {
    return false;
  }
  return messages.some((message) => (
    message.role === 'assistant'
    && !message.message_metadata?.stream_interrupted
    && message.message_metadata?.generation_id === generationId
  ));
}

export function withoutAssistantMessagesForGeneration(
  messages: Message[],
  generationId?: string | null,
): Message[] {
  if (!generationId) return messages;
  return messages.filter((message) => !(
    message.role === 'assistant'
    && message.message_metadata?.generation_id === generationId
  ));
}

async function errorMessageFromResponse(response: Response, fallback: string): Promise<string> {
  try {
    const body = await response.json();
    if (typeof body?.detail === 'string' && body.detail.trim()) {
      return body.detail;
    }
    if (Array.isArray(body?.detail)) {
      const first = body.detail[0];
      if (typeof first?.msg === 'string' && first.msg.trim()) {
        return first.msg;
      }
    }
  } catch {
    // The start endpoint may return a non-JSON body.
  }
  return fallback;
}

export interface ChatLoadResult {
  messages: Message[];
  documentSelections: DocumentSelection[];
  lastUserWebSearch: boolean;
}

export interface ChatLoadOptions {
  force?: boolean;
  silent?: boolean;
  skipResume?: boolean;
}

/** Per-turn hints about what the user is looking at. Add fields as new screens announce themselves. */
export interface ClientUiContext {
  open_bundle_id?: string;
}

export interface SendMessageStartResult {
  started?: boolean;
  conflict?: boolean;
  error?: string;
  chatGuid?: string;
}

export interface SendMessageOptions {
  forceNewChat?: boolean;
  userMessageMetadata?: Record<string, unknown>;
  /** Opt this prompt into public-web research. Stored on the user message. */
  webSearch?: boolean;
  resumeGenerationId?: string;
  /** Side-panel only: ephemeral UI hints for this turn (not saved on the chat message). */
  uiContext?: ClientUiContext;
  /** When the turn being resumed began, as the server recorded it. */
  resumeStartedAt?: string | null;
  /** Server status of the generation being resumed, if known. */
  resumeGenerationStatus?: string | null;
  /** Fires when the start/claim handshake finishes, not when the stream ends. */
  onStartSettled?: (result: SendMessageStartResult) => void;
}

export interface ProducedDraftDocument {
  documentGuid: string;
  title?: string;
  version?: number;
  action?: 'create';
  chatGuid: string | null;
  generationId: string | null;
  producedAt: number;
}

export interface SendMessageResult {
  conflict?: boolean;
}

export interface UseChat {
  messages: Message[];
  chatHistory: ChatSession[];
  isLoading: boolean;
  isStreaming: boolean;
  isStreamingCurrentChat: boolean;
  isCurrentChatTurnActive: boolean;
  hasActiveGeneration: boolean;
  canAbortStreaming: boolean;
  stopRequested: boolean;
  streamingChatGuid: string | null;
  sendMessage: (content: string, documentSelections?: BackendDocumentSelection[], options?: SendMessageOptions) => Promise<SendMessageResult | void>;
  streamingContent: string;
  streamingQuickReplyQuestions: QuickReplyQuestion[];
  streamingTemplateChoices: ActiveTemplateChoices | null;
  streamingThinkingSteps: ThinkingStep[];
  streamingTextInsertions: TimelineTextInsertion[];
  streamingWaitMarkers: TimelineWaitMarker[];
  finalAnswerConfirmed: boolean;
  /** Replayed history for this attach: show it as found, then type only new tokens. */
  streamCatchingUp: boolean;
  /** Epoch ms the in-flight turn began, however long ago this tab joined it. */
  turnStartedAt: number | null;
  /** How long the turn worked, from the server, frozen when the answer was confirmed. */
  turnWorkedDurationMs: number | null;
  abortStreaming: () => void;
  startNewChat: () => void;
  loadChat: (chatGuid: string, options?: ChatLoadOptions) => Promise<ChatLoadResult | undefined>;
  suppressRouteChatLoadRef: { current: boolean };
  loadMoreChatHistory: () => Promise<void>;
  searchChatHistory: (query: string) => void;
  renameChat: (chatGuid: string, title: string) => Promise<void>;
  deleteChat: (chatGuid: string) => Promise<void>;
  chatHistoryHasMore: boolean;
  isLoadingMoreChatHistory: boolean;
  chatHistorySearchQuery: string;
  chatHistorySearchResults: ChatSession[];
  chatHistorySearchHasMore: boolean;
  focusMessage: { id: number; token: number; query?: string } | null;
  requestFocusMessage: (messageId: number | undefined, query?: string) => void;
  currentChatGuid: string | null;
  isThinking: boolean;
  latestProducedDraftDocument: ProducedDraftDocument | null;
  clearLatestProducedDraftDocument: (documentGuid?: string) => void;
}

export const MAX_CHAT_TITLE_LENGTH = 80;
const CHAT_HISTORY_PAGE_SIZE = 30;
const STREAM_CONTENT_UPDATE_INTERVAL_MS = 32;
const STREAM_INTERRUPTED_MESSAGE = 'The model connection dropped while streaming. Please retry if you need the rest of the answer.';
const STREAM_REQUEST_FAILED_MESSAGE = 'Sorry, I encountered an error while processing your request. Please try again.';
const CHAT_MESSAGES_PAGE_SIZE = 200;
const EMPTY_CHAT_RETRY_MS = 400;
const GENERATION_STOPPED_METADATA = {
  generation_stopped: true,
};
const WORD_EXPORT_FILENAME = 'case-partner-response.docx';
const WORD_EXPORT_MARKER_RE = /<!--\s*crimson-word-export\s*:\s*(self|previous(?:\s*:\s*[1-9]\d*)?)\s*-->/i;
const QUICK_REPLIES_MARKER_RE = /<!--\s*crimson-quick-replies\s*:\s*([\s\S]*?)\s*-->/i;
const TEMPLATE_CHOICES_MARKER_RE = /<!--\s*crimson-template-choices\s*:\s*([\s\S]*?)\s*-->/i;
const ASSISTANT_UI_MARKER_START_RE = /<!--\s*crimson-(?:word-export|quick-replies|template-choices)\s*:/i;
const getCurrentTime = () => (
  typeof performance !== 'undefined' ? performance.now() : Date.now()
);

function chatSessionSortTime(chat: ChatSession): number {
  return (chat.updatedAt || chat.createdAt).getTime();
}

function mapChatHistoryResponse(data: any[]): ChatSession[] {
  const timezone = getUserTimezone();
  const chatSessions = data.map((chat: any) => {
    const generationId = chat.active_generation_id || null;
    const status = chat.active_generation_status || null;
    return {
      guid: chat.guid,
      title: chat.title || 'New Chat',
      messages: [],
      createdAt: isoToZonedDate(chat.created_at, timezone) || new Date(),
      updatedAt: isoToZonedDate(chat.updated_at, timezone) || undefined,
      activeGenerationId: generationId,
      activeGenerationStatus: generationResumeShouldLookStopped(
        status,
        Boolean(readStopLatch(generationId)),
      ) ? 'stopping' : status,
      activeGenerationStartedAt: chat.active_generation_started_at || null,
      searchSnippet: typeof chat.search_snippet === 'string' && chat.search_snippet
        ? chat.search_snippet
        : undefined,
      searchMessageId: typeof chat.search_message_id === 'number'
        ? chat.search_message_id
        : undefined,
    } satisfies ChatSession;
  });

  chatSessions.sort((a, b) => chatSessionSortTime(b) - chatSessionSortTime(a));
  return chatSessions;
}

function mergeChatHistoryPages(existing: ChatSession[], incoming: ChatSession[]): ChatSession[] {
  const incomingByGuid = new Map(incoming.map((chat) => [chat.guid, chat]));
  const existingGuids = new Set(existing.map((chat) => chat.guid));
  const newChats = incoming.filter((chat) => !existingGuids.has(chat.guid));
  const merged = [
    ...newChats,
    ...existing.map((chat) => incomingByGuid.get(chat.guid) ?? chat),
  ];
  merged.sort((a, b) => chatSessionSortTime(b) - chatSessionSortTime(a));
  return merged;
}

async function fetchChatHistoryPage(
  matterGuid: string,
  offset: number,
  limit: number,
  search?: string,
): Promise<ChatSession[]> {
  const response = await backendClient.get('/case-partner', {
    params: {
      matter_guid: matterGuid,
      limit,
      offset,
      ...(search ? { search } : {}),
    },
  });
  return mapChatHistoryResponse(response.data);
}

export const isoToEpochMs = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const parsed = Date.parse(iso);
  return Number.isNaN(parsed) ? null : parsed;
};

const draftMetadataRow = (metadata: any) => {
  if (!metadata?.draft_document_guid && !metadata?.draft_document_job_guid) return null;
  if (metadata.draft_document_action === 'inspect') return null;
  return Object.fromEntries(
    Object.entries(metadata).filter(([key]) => (
      key.startsWith('draft_document_') && key !== 'draft_documents'
    )),
  );
};

const draftMetadataRows = (metadata: any) => {
  if (!metadata) return [];
  if (Array.isArray(metadata.draft_documents)) return metadata.draft_documents;
  const row = draftMetadataRow(metadata);
  return row ? [row] : [];
};

// Where a reloaded assistant message's text sits, and whether it is the answer.
// Both a finished and a stopped turn put their text after that message's steps,
// but only a finished one has an answer to lift out of the work trail.
const completedMessagePlacement = (metadata: any) => {
  const isFinal = metadata?.message_phase === 'final_answer';
  return {
    afterAllSteps: isFinal || Boolean(metadata?.generation_stopped),
    isFinal,
  };
};

const mergeMessageMetadata = (current: any, next: any) => {
  const merged = { ...(current || {}), ...(next || {}) };
  const rows = [...draftMetadataRows(current), ...draftMetadataRows(next)];
  if (rows.length === 0) return merged;

  const byJobGuid = new Map<string, any>();
  for (const row of rows) {
    const jobGuid = row?.draft_document_job_guid;
    if (typeof jobGuid !== 'string' || !jobGuid) continue;
    byJobGuid.set(jobGuid, { ...byJobGuid.get(jobGuid), ...row });
  }
  const mergedRows = [...byJobGuid.values()];
  if (mergedRows.length === 0) return merged;

  for (const key of Object.keys(merged)) {
    if (key.startsWith('draft_document_') && key !== 'draft_documents') delete merged[key];
  }
  if (mergedRows.length === 1) Object.assign(merged, mergedRows[0]);
  else merged.draft_documents = mergedRows;
  return merged;
};

/** Auto-expand draft cards only for a single draft in the round; keep multi-draft rounds collapsed. */
const shouldDefaultExpandDraftCards = (metadata: any) => {
  if (!metadata || typeof metadata !== 'object') return false;
  if (Array.isArray(metadata.draft_documents)) {
    return metadata.draft_documents.filter(draftMetadataRow).length === 1;
  }
  return Boolean(draftMetadataRow(metadata));
};

const extractWordExportMarker = (content: string) => {
  const match = WORD_EXPORT_MARKER_RE.exec(content);
  if (!match) return { content };
  const markerTarget = match[1].toLowerCase().replace(/\s+/g, '');
  const target = markerTarget.startsWith('previous') ? 'previous' : 'self';
  const assistantResponseOffset = markerTarget.startsWith('previous:')
    ? Number(markerTarget.split(':')[1])
    : undefined;
  const wordExport: {
    target: 'self' | 'previous';
    filename: string;
    include_citations: boolean;
    assistant_response_offset?: number;
  } = {
    target,
    filename: WORD_EXPORT_FILENAME,
    include_citations: true,
  };
  if (target === 'previous' && assistantResponseOffset && Number.isFinite(assistantResponseOffset)) {
    wordExport.assistant_response_offset = assistantResponseOffset;
  }

  return {
    content: content.replace(WORD_EXPORT_MARKER_RE, '').trimEnd(),
    metadata: {
      word_export: wordExport,
    },
  };
};

const extractQuickRepliesMarker = (content: string) => {
  const match = QUICK_REPLIES_MARKER_RE.exec(content);
  if (!match) return { content };

  let metadata: { quick_replies: { questions: QuickReplyQuestion[] } } | undefined;

  try {
    const rawQuickReplies = JSON.parse(match[1]);
    if (rawQuickReplies && typeof rawQuickReplies === 'object' && !Array.isArray(rawQuickReplies)) {
      const quickReplyRecord = rawQuickReplies as Record<string, unknown>;
      const questions = normalizeQuickReplyQuestions(quickReplyRecord.questions);

      if (questions.length > 0) {
        metadata = {
          quick_replies: {
            questions,
          },
        };
      }
    }
  } catch (error) {
    console.warn('Ignoring invalid Case Partner quick replies marker:', error);
  }

  return {
    content: content.replace(QUICK_REPLIES_MARKER_RE, '').trimEnd(),
    metadata,
  };
};

const extractTemplateChoicesMarker = (content: string) => {
  const match = TEMPLATE_CHOICES_MARKER_RE.exec(content);
  if (!match) return { content };

  let metadata: { template_choices: { question: string; allow_none: boolean; templates: ReturnType<typeof normalizeTemplateChoices> } } | undefined;

  try {
    const rawTemplateChoices = JSON.parse(match[1]);
    if (rawTemplateChoices && typeof rawTemplateChoices === 'object' && !Array.isArray(rawTemplateChoices)) {
      const templateChoiceRecord = rawTemplateChoices as Record<string, unknown>;
      const templates = normalizeTemplateChoices(templateChoiceRecord.templates);
      const question = typeof templateChoiceRecord.question === 'string'
        ? templateChoiceRecord.question.trim()
        : '';

      if (templates.length > 0) {
        metadata = {
          template_choices: {
            question: question || 'Which template should I use?',
            allow_none: templateChoiceRecord.allow_none !== false,
            templates,
          },
        };
      }
    }
  } catch (error) {
    console.warn('Ignoring invalid Case Partner template choices marker:', error);
  }

  return {
    content: content.replace(TEMPLATE_CHOICES_MARKER_RE, '').trimEnd(),
    metadata,
  };
};

const stripIncompleteAssistantMarker = (content: string) => {
  const markerStart = content.search(ASSISTANT_UI_MARKER_START_RE);
  if (markerStart === -1) return content;

  const markerEnd = content.indexOf('-->', markerStart);
  if (markerEnd !== -1) return content;

  return content.slice(0, markerStart).trimEnd();
};

export const extractAssistantMarkers = (content: string) => {
  const wordExportResult = extractWordExportMarker(content);
  const quickRepliesResult = extractQuickRepliesMarker(wordExportResult.content);
  const templateChoicesResult = extractTemplateChoicesMarker(quickRepliesResult.content);

  return {
    content: stripIncompleteAssistantMarker(templateChoicesResult.content),
    metadata: {
      ...(wordExportResult.metadata || {}),
      ...(quickRepliesResult.metadata || {}),
      ...(templateChoicesResult.metadata || {}),
    },
  };
};

export const buildInterruptedAssistantMessagePresentation = (
  accumulatedResponse: string,
  errorMessage: string,
) => {
  const markerResult = extractAssistantMarkers(accumulatedResponse);
  const markerMetadata = markerResult.metadata ? { ...markerResult.metadata } : undefined;
  if (markerMetadata) delete markerMetadata.word_export;
  const metadata = markerMetadata && Object.keys(markerMetadata).length > 0
    ? markerMetadata
    : undefined;
  const content = markerResult.content.trim()
    ? `${markerResult.content}\n\n---\n\n${errorMessage}`
    : errorMessage;

  return {
    content,
    metadata,
  };
};

/**
 * Thinking steps for a turn that ended before its tools did.
 *
 * A stop and a stream failure both end the turn where it stands, and the
 * server seals either one the same way when it finalizes. Leaving a tool at
 * `in_progress` here would show it working until the next reload swapped in
 * the server's copy, so both paths read their steps through this.
 */
export const finalStepsForEndedTurn = <T extends { type?: string; data?: any }>(
  steps: T[],
): T[] | undefined => (steps.length > 0 ? sealUnfinishedSteps(steps) : undefined);

export const buildStoppedAssistantMessagePresentation = (
  accumulatedResponse: string,
  metadata: Record<string, any>,
) => {
  const markerResult = extractAssistantMarkers(accumulatedResponse);
  const assistantMetadata = {
    ...(metadata || {}),
    ...(markerResult.metadata || {}),
    ...GENERATION_STOPPED_METADATA,
  };

  if (!markerResult.content && Object.keys(assistantMetadata).length === 0) {
    return null;
  }

  return {
    content: markerResult.content,
    metadata: assistantMetadata,
  };
};

const extractStreamingAssistantPresentation = (content: string) => {
  const markerResult = extractAssistantMarkers(content);
  return {
    content: markerResult.content,
    quickReplyQuestions: quickReplyQuestionsFromMetadata(markerResult.metadata),
    templateChoices: templateChoicesFromMetadata(markerResult.metadata),
  };
};

const waitForBrowserPaint = () => new Promise<void>((resolve) => {
  if (typeof requestAnimationFrame !== 'function') {
    setTimeout(resolve, 0);
    return;
  }

  requestAnimationFrame(() => {
    setTimeout(resolve, 0);
  });
});

export const useChat = (): UseChat => {
  const { selectedCase } = useCaseContext();
  const [messages, setMessages] = useState<Message[]>([]);
  const [chatHistory, setChatHistory] = useState<ChatSession[]>([]);
  const [chatHistoryHasMore, setChatHistoryHasMore] = useState(false);
  const [isLoadingMoreChatHistory, setIsLoadingMoreChatHistory] = useState(false);
  const [chatHistorySearchQuery, setChatHistorySearchQuery] = useState('');
  const [chatHistorySearchResults, setChatHistorySearchResults] = useState<ChatSession[]>([]);
  const [chatHistorySearchHasMore, setChatHistorySearchHasMore] = useState(false);
  const [focusMessage, setFocusMessage] = useState<{ id: number; token: number; query?: string } | null>(null);
  const focusMessageTokenRef = useRef(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [canAbortStreaming, setCanAbortStreaming] = useState(false);
  const canAbortStreamingRef = useRef(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [streamingQuickReplyQuestions, setStreamingQuickReplyQuestions] = useState<QuickReplyQuestion[]>([]);
  const [streamingTemplateChoices, setStreamingTemplateChoices] = useState<ActiveTemplateChoices | null>(null);
  const [streamingThinkingSteps, setStreamingThinkingSteps] = useState<ThinkingStep[]>([]);
  const [streamingTextInsertions, setStreamingTextInsertions] = useState<TimelineTextInsertion[]>([]);
  const [streamingWaitMarkers, setStreamingWaitMarkers] = useState<TimelineWaitMarker[]>([]);
  const [finalAnswerConfirmed, setFinalAnswerConfirmed] = useState(false);
  const [streamCatchingUp, setStreamCatchingUp] = useState(false);
  const streamCatchingUpRef = useRef(false);
  const clearCatchUpAfterPaintRef = useRef(false);
  const updateStreamCatchingUp = useCallback((next: boolean) => {
    streamCatchingUpRef.current = next;
    setStreamCatchingUp(next);
  }, []);
  const [currentChatGuid, setCurrentChatGuid] = useState<string | null>(null);
  const currentChatGuidRef = useRef<string | null>(null);
  const [streamingChatGuid, setStreamingChatGuid] = useState<string | null>(null);
  const streamingChatGuidRef = useRef<string | null>(null);
  const [latestProducedDraftDocument, setLatestProducedDraftDocument] = useState<ProducedDraftDocument | null>(null);
  const [isPendingVisibleNewChat, setIsPendingVisibleNewChat] = useState(false);
  const isPendingVisibleNewChatRef = useRef(false);
  const [stopRequested, setStopRequested] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const detachRequestedRef = useRef(false);
  const lastEventIdRef = useRef<string | null>(null);
  const sendInFlightRef = useRef(false);
  // TODO: Consolidate streaming refs/state into a reducer or small stream controller.
  const finalThinkingStepsRef = useRef<ThinkingStep[]>([]);
  const accumulatedResponseRef = useRef('');
  const activeGenerationIdRef = useRef<string | null>(null);
  const [turnStartedAt, setTurnStartedAt] = useState<number | null>(null);
  const turnStartedAtRef = useRef<number | null>(null);
  const [turnWorkedDurationMs, setTurnWorkedDurationMs] = useState<number | null>(null);
  const turnWorkedDurationMsRef = useRef<number | null>(null);
  const hasPersistedStopRef = useRef(false);
  const hasReceivedFirstOutput = useRef(false);
  const bundleMetadataRef = useRef<{ bundle_url: string; bundle_title: string } | null>(null);
  const deadlineMetadataRef = useRef<{ deadline_url: string; deadline_title: string } | null>(null);
  // Dedupe bundle-refresh signals within a stream so live and snapshot-reconnect paths don't
  // double-fire (and so text deltas don't re-fire on every sync).
  const notifiedUiChangeRef = useRef<Set<string>>(new Set());
  const draftDocumentMetadataRef = useRef<Record<string, any> | null>(null);
  const stopRequestedRef = useRef(false);
  // Set only on the Stop click, not on attach-as-stopped. Live events after
  // the click must not reopen chips the click just sealed.
  const liveStopSealedRef = useRef(false);
  const streamingWaitMarkersRef = useRef<TimelineWaitMarker[]>([]);
  const finishedGenerationIdRef = useRef<string | null>(null);
  const streamGenerationRef = useRef(0);
  const loadChatRequestRef = useRef(0);
  const suppressRouteChatLoadRef = useRef(false);
  const activeChatMatterGuidRef = useRef<string | null>(null);
  const lastSelectedCaseRef = useRef<string | null>(null);
  const chatHistoryRef = useRef<ChatSession[]>([]);
  const loadMoreChatHistoryInFlightRef = useRef(false);
  const chatHistorySearchQueryRef = useRef('');
  const chatHistorySearchResultsRef = useRef<ChatSession[]>([]);
  const chatHistorySearchRequestRef = useRef(0);
  const chatHistoryLoadRequestRef = useRef(0);
  chatHistoryRef.current = chatHistory;
  chatHistorySearchQueryRef.current = chatHistorySearchQuery;
  chatHistorySearchResultsRef.current = chatHistorySearchResults;

  const updateCurrentChatGuid = useCallback((chatGuid: string | null) => {
    currentChatGuidRef.current = chatGuid;
    setCurrentChatGuid(chatGuid);
  }, []);

  const updateStreamingChatGuid = useCallback((chatGuid: string | null) => {
    streamingChatGuidRef.current = chatGuid;
    setStreamingChatGuid(chatGuid);
  }, []);

  const updatePendingVisibleNewChat = useCallback((isPending: boolean) => {
    isPendingVisibleNewChatRef.current = isPending;
    setIsPendingVisibleNewChat(isPending);
  }, []);

  const updateStopRequested = useCallback((requested: boolean) => {
    stopRequestedRef.current = requested;
    setStopRequested(requested);
  }, []);

  const clearLatestProducedDraftDocument = useCallback((documentGuid?: string) => {
    setLatestProducedDraftDocument((current) => {
      if (!current) return null;
      if (documentGuid && current.documentGuid !== documentGuid) return current;
      return null;
    });
  }, []);

  const resetChatState = useCallback(() => {
    streamGenerationRef.current += 1;
    loadChatRequestRef.current += 1;
    sendInFlightRef.current = false;

    if (abortControllerRef.current) {
      detachRequestedRef.current = true;
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    suppressRouteChatLoadRef.current = true;
    setMessages([]);
    updateCurrentChatGuid(null);
    updateStreamingChatGuid(null);
    updatePendingVisibleNewChat(false);
    setStreamingContent('');
    setStreamingQuickReplyQuestions([]);
    setStreamingTemplateChoices(null);
    setStreamingThinkingSteps([]);
    setStreamingTextInsertions([]);
    setStreamingWaitMarkers([]);
    setFinalAnswerConfirmed(false);
    turnWorkedDurationMsRef.current = null;
    setTurnWorkedDurationMs(null);
    updateStreamCatchingUp(false);
    setIsStreaming(false);
    canAbortStreamingRef.current = false;
    setCanAbortStreaming(false);
    setIsThinking(false);
    activeChatMatterGuidRef.current = null;
    finalThinkingStepsRef.current = [];
    streamingWaitMarkersRef.current = [];
    liveStopSealedRef.current = false;
    accumulatedResponseRef.current = '';
    activeGenerationIdRef.current = null;
    hasPersistedStopRef.current = false;
    hasReceivedFirstOutput.current = false;
    bundleMetadataRef.current = null;
    deadlineMetadataRef.current = null;
    notifiedUiChangeRef.current = new Set();
    draftDocumentMetadataRef.current = null;
    setLatestProducedDraftDocument(null);
    updateStopRequested(false);
  }, [updateCurrentChatGuid, updatePendingVisibleNewChat, updateStopRequested, updateStreamCatchingUp, updateStreamingChatGuid]);

  useEffect(() => {
    if (!clearCatchUpAfterPaintRef.current) return;
    clearCatchUpAfterPaintRef.current = false;
    updateStreamCatchingUp(false);
  }, [
    streamingContent,
    streamingTextInsertions,
    streamingThinkingSteps,
    updateStreamCatchingUp,
  ]);

  // Load chat history
  // showLoadingState: when false, updates chat history silently without showing loading indicator
  const loadChatHistory = useCallback(async (showLoadingState: boolean = true) => {
    if (!selectedCase) return;

    const requestId = ++chatHistoryLoadRequestRef.current;
    const matterGuid = selectedCase;

    if (showLoadingState) {
      setIsLoading(true);
    }
    try {
      const chatSessions = await fetchChatHistoryPage(matterGuid, 0, CHAT_HISTORY_PAGE_SIZE);
      if (chatHistoryLoadRequestRef.current !== requestId) return;
      if (showLoadingState) {
        setChatHistory(chatSessions);
        setChatHistoryHasMore(chatSessions.length === CHAT_HISTORY_PAGE_SIZE);
      } else {
        setChatHistory((prev) => mergeChatHistoryPages(prev, chatSessions));
      }
    } catch (error) {
      if (chatHistoryLoadRequestRef.current !== requestId) return;
      console.error('Error loading chat history:', error);
    } finally {
      if (showLoadingState) {
        setIsLoading(false);
      }
    }
  }, [selectedCase]);

  const loadMoreChatHistory = useCallback(async () => {
    const searchQuery = chatHistorySearchQueryRef.current.trim();
    const searching = searchQuery.length > 0;
    if (
      !selectedCase ||
      loadMoreChatHistoryInFlightRef.current ||
      (searching ? !chatHistorySearchHasMore : !chatHistoryHasMore)
    ) {
      return;
    }

    loadMoreChatHistoryInFlightRef.current = true;
    setIsLoadingMoreChatHistory(true);
    const matterGuid = selectedCase;
    try {
      const offset = searching
        ? chatHistorySearchResultsRef.current.length
        : chatHistoryRef.current.filter((chat) => !chat.isTemporary).length;
      const chatSessions = await fetchChatHistoryPage(
        matterGuid,
        offset,
        CHAT_HISTORY_PAGE_SIZE,
        searching ? searchQuery : undefined,
      );
      if (lastSelectedCaseRef.current !== matterGuid) return;
      if (searching) {
        if (chatHistorySearchQueryRef.current.trim() !== searchQuery) return;
        setChatHistorySearchResults((prev) => {
          const existingGuids = new Set(prev.map((chat) => chat.guid));
          return [...prev, ...chatSessions.filter((chat) => !existingGuids.has(chat.guid))];
        });
        setChatHistorySearchHasMore(chatSessions.length === CHAT_HISTORY_PAGE_SIZE);
      } else {
        setChatHistory((prev) => {
          const existingGuids = new Set(prev.map((chat) => chat.guid));
          return [...prev, ...chatSessions.filter((chat) => !existingGuids.has(chat.guid))];
        });
        setChatHistoryHasMore(chatSessions.length === CHAT_HISTORY_PAGE_SIZE);
      }
    } catch (error) {
      if (lastSelectedCaseRef.current !== matterGuid) return;
      console.error('Error loading more chat history:', error);
      toast.error(apiErrorMessage(error) || 'Failed to load more chats');
    } finally {
      loadMoreChatHistoryInFlightRef.current = false;
      setIsLoadingMoreChatHistory(false);
    }
  }, [chatHistoryHasMore, chatHistorySearchHasMore, selectedCase]);

  const requestFocusMessage = useCallback((messageId: number | undefined, query?: string) => {
    if (!messageId) {
      setFocusMessage(null);
      return;
    }
    focusMessageTokenRef.current += 1;
    setFocusMessage({
      id: messageId,
      token: focusMessageTokenRef.current,
      query: query?.trim() || undefined,
    });
  }, []);

  const searchChatHistory = useCallback((query: string) => {
    const nextQuery = query.trim();
    if (!selectedCase || !nextQuery) {
      chatHistorySearchRequestRef.current += 1;
      setChatHistorySearchQuery('');
      setChatHistorySearchResults([]);
      setChatHistorySearchHasMore(false);
      return;
    }

    const requestId = chatHistorySearchRequestRef.current + 1;
    chatHistorySearchRequestRef.current = requestId;
    void (async () => {
      try {
        const chatSessions = await fetchChatHistoryPage(
          selectedCase,
          0,
          CHAT_HISTORY_PAGE_SIZE,
          nextQuery,
        );
        if (chatHistorySearchRequestRef.current !== requestId) return;
        setChatHistorySearchQuery(nextQuery);
        setChatHistorySearchResults(chatSessions);
        setChatHistorySearchHasMore(chatSessions.length === CHAT_HISTORY_PAGE_SIZE);
      } catch (error) {
        if (chatHistorySearchRequestRef.current !== requestId) return;
        console.error('Error searching chat history:', error);
        toast.error(apiErrorMessage(error) || 'Failed to search chats');
        setChatHistorySearchQuery(nextQuery);
        setChatHistorySearchResults([]);
        setChatHistorySearchHasMore(false);
      }
    })();
  }, [selectedCase]);

  // Load chat history when case changes
  useEffect(() => {
    if (selectedCase) {
      if (lastSelectedCaseRef.current && lastSelectedCaseRef.current !== selectedCase) {
        resetChatState();
      }

      lastSelectedCaseRef.current = selectedCase;
      chatHistorySearchRequestRef.current += 1;
      setChatHistory([]);
      setChatHistoryHasMore(false);
      setChatHistorySearchQuery('');
      setChatHistorySearchResults([]);
      setChatHistorySearchHasMore(false);
      loadChatHistory();
    }
  }, [selectedCase, loadChatHistory, resetChatState]);

  const hasActiveGeneration = chatHistory.some((chat) => (
    isActiveGenerationStatus(chat.activeGenerationStatus)
  ));

  useEffect(() => {
    if (!hasActiveGeneration) return undefined;
    const timer = window.setInterval(() => {
      void loadChatHistory(false);
    }, 4000);
    return () => window.clearInterval(timer);
  }, [hasActiveGeneration, loadChatHistory]);

  // Load a specific chat
  const loadChat = async (chatGuid: string, options: ChatLoadOptions = {}) => {
    const isChangingVisibleChat = (
      currentChatGuid !== chatGuid ||
      activeChatMatterGuidRef.current !== selectedCase
    );
    const isInFlightSameChat = (
      sendInFlightRef.current &&
      streamingChatGuidRef.current === chatGuid
    );

    // Skip if already viewing this chat
    if (
      currentChatGuid === chatGuid &&
      activeChatMatterGuidRef.current === selectedCase &&
      !options.force
    ) {
      return {
        messages,
        documentSelections: getDocumentSelectionsFromMessages(messages),
        lastUserWebSearch: getWebSearchFromMessages(messages),
      };
    }

    if (isInFlightSameChat && !isChangingVisibleChat && messages.length > 0) {
      return {
        messages,
        documentSelections: getDocumentSelectionsFromMessages(messages),
        lastUserWebSearch: getWebSearchFromMessages(messages),
      };
    }

    const requestId = ++loadChatRequestRef.current;
    
    if (!options.silent) {
      setIsLoading(true);
    }
    if (isChangingVisibleChat) {
      if (!isInFlightSameChat && abortControllerRef.current) {
        detachRequestedRef.current = true;
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      updatePendingVisibleNewChat(false);
      suppressRouteChatLoadRef.current = false;
      updateCurrentChatGuid(chatGuid);
      activeChatMatterGuidRef.current = selectedCase ?? null;
      setMessages([]);
    }

    try {
      // Heal orphans before reading messages so a recovered assistant row is
      // visible on this load instead of only after leaving and re-entering.
      let generationId: string | null = null;
      let generationStatus: string | null = null;
      let generationStartedAt: string | null = null;
      try {
        const chatMeta = await backendClient.get(`/case-partner/${chatGuid}`, {
          params: { matter_guid: selectedCase },
        });
        if (
          loadChatRequestRef.current !== requestId ||
          currentChatGuidRef.current !== chatGuid
        ) {
          return;
        }
        generationId = chatMeta.data?.active_generation_id || null;
        generationStatus = chatMeta.data?.active_generation_status || null;
        generationStartedAt = chatMeta.data?.active_generation_started_at || null;
      } catch (resumeError) {
        console.error('Error resuming Case Partner generation:', resumeError);
      }

      const fetchMessagePages = async () => {
        const responseData: any[] = [];
        let offset = 0;

        while (true) {
          const response = await backendClient.get(`/case-partner/${chatGuid}/messages`, {
            params: {
              matter_guid: selectedCase,
              limit: CHAT_MESSAGES_PAGE_SIZE,
              offset,
            },
          });

          responseData.push(...response.data);

          if (response.data.length < CHAT_MESSAGES_PAGE_SIZE) {
            break;
          }

          offset += CHAT_MESSAGES_PAGE_SIZE;
        }

        return responseData;
      };

      let responseData = await fetchMessagePages();
      if (responseData.length === 0) {
        await new Promise((resolve) => window.setTimeout(resolve, EMPTY_CHAT_RETRY_MS));
        if (
          loadChatRequestRef.current !== requestId ||
          currentChatGuidRef.current !== chatGuid
        ) {
          return;
        }
        responseData = await fetchMessagePages();
      }

      if (
        loadChatRequestRef.current !== requestId ||
        currentChatGuidRef.current !== chatGuid
      ) {
        return;
      }
      
      // Get the user's timezone
      const timezone = getUserTimezone();
      
      const rawMessages = responseData.map((msg: any) => {
        // Merge reasoning steps and tool calls into thinkingSteps
        const thinkingSteps: ThinkingStep[] = [];

        // Add tool calls
        const toolCalls = msg.tool_calls ?? [];
        const storedRoundNum = toolCalls.find((toolCall: { round_num?: number }) => (
          typeof toolCall.round_num === 'number'
        ))?.round_num;

        // Add reasoning steps
        thinkingSteps.push(...reasoningStepsFromStoredRound(msg.reasoning_steps, storedRoundNum));
        for (const toolCall of toolCalls) {
          thinkingSteps.push({
            type: 'tool',
            data: toolCall,
            round_num: toolCall.round_num,
            summary_index: 999
          });
        }

        // Interrupted turns (user stop or failed-orphan heal) may stash live
        // thinking under stopped_thinking_steps. Do not require generation_stopped
        // so crash-persisted partials still hydrate thinking without a Stopped label.
        const stoppedThinkingSteps = Array.isArray(msg.message_metadata?.stopped_thinking_steps)
          ? msg.message_metadata.stopped_thinking_steps
          : [];

        return {
          id: msg.message_id,
          messageIds: typeof msg.message_id === 'number' ? [msg.message_id] : [],
          content: msg.content,
          role: msg.role,
          timestamp: isoToZonedDate(msg.created_at, timezone) || new Date(),
          message_metadata: msg.message_metadata,
          document_guids: msg.document_guids,  // Include document selections for restoring state
          thinkingSteps: thinkingSteps.length > 0
            ? thinkingSteps
            : (stoppedThinkingSteps.length > 0 ? stoppedThinkingSteps : undefined),
        };
      });

      // Merge consecutive assistant messages into single message
      const mergedMessages: Message[] = [];
      let currentMergedMessage: Message | null = null;

      for (const msg of rawMessages) {
        if (msg.role === 'assistant') {
          if (currentMergedMessage && currentMergedMessage.role === 'assistant') {
            // Merge with the current assistant message
            // Combine content (only add if not empty)
            if (msg.content) {
              if (currentMergedMessage.content) {
                currentMergedMessage.content += '\n\n' + msg.content;
              } else {
                currentMergedMessage.content = msg.content;
              }
            }

            currentMergedMessage.timelineInsertions = addRoundTimelineInsertion(
              currentMergedMessage.timelineInsertions || [],
              currentMergedMessage.thinkingSteps?.length || 0,
              msg.thinkingSteps || [],
              msg.content,
              completedMessagePlacement(msg.message_metadata),
            );

            if (msg.thinkingSteps && msg.thinkingSteps.length > 0) {
              currentMergedMessage.thinkingSteps = [
                ...(currentMergedMessage.thinkingSteps || []),
                ...msg.thinkingSteps,
              ];
            }

            if (typeof msg.id === 'number') {
              currentMergedMessage.messageIds = [
                ...(currentMergedMessage.messageIds || []),
                msg.id,
              ];
            }

            // Update timestamp to the latest
            currentMergedMessage.timestamp = msg.timestamp;

            // Merge message_metadata (later messages take precedence)
            if (msg.message_metadata) {
              currentMergedMessage.message_metadata = mergeMessageMetadata(
                currentMergedMessage.message_metadata,
                msg.message_metadata,
              );
            }
          } else {
            // Start a new merged assistant message
            if (currentMergedMessage) {
              mergedMessages.push(currentMergedMessage);
            }
            currentMergedMessage = {
              ...msg,
              thinkingSteps: msg.thinkingSteps || [],
              timelineInsertions: addRoundTimelineInsertion(
                [],
                0,
                msg.thinkingSteps || [],
                msg.content,
                completedMessagePlacement(msg.message_metadata),
              ),
            };
          }
        } else {
          // Non-assistant message
          if (currentMergedMessage) {
            mergedMessages.push(currentMergedMessage);
            currentMergedMessage = null;
          }
          mergedMessages.push(msg);
        }
      }

      // Don't forget the last message if it's an assistant message
      if (currentMergedMessage) {
        mergedMessages.push(currentMergedMessage);
      }

      if (generationId && !isActiveGenerationStatus(generationStatus)) {
        clearStopLatch(generationId);
      }

      const shouldResume = Boolean(
        !options.skipResume
        && generationId
        && isActiveGenerationStatus(generationStatus)
      );
      const visibleMessages = shouldResume
        ? withoutAssistantMessagesForGeneration(mergedMessages, generationId)
        : mergedMessages;
      setMessages(visibleMessages);
      updateCurrentChatGuid(chatGuid);
      activeChatMatterGuidRef.current = selectedCase ?? null;

      const resumeAsStopped = generationResumeShouldLookStopped(
        generationStatus,
        Boolean(readStopLatch(generationId)),
      );
      const resumeStatus = resumeAsStopped ? 'stopping' : generationStatus;

      setChatHistory((prev) => prev.map((chat) => (
        chat.guid === chatGuid
          ? {
              ...chat,
              activeGenerationId: shouldResume ? generationId : null,
              activeGenerationStatus: shouldResume ? resumeStatus : null,
              activeGenerationStartedAt: shouldResume ? generationStartedAt : null,
            }
          : chat
      )));
      if (shouldResume && generationId) {
        void attachGeneration(
          generationId,
          generationStartedAt,
          resumeStatus,
        );
      } else if (generationId) {
        finishedGenerationIdRef.current = generationId;
      }
      
      // Return the merged messages so they can be accessed by the caller
      return {
        messages: visibleMessages,
        // Extract document selections from the last user message that has them
        documentSelections: getDocumentSelectionsFromMessages(visibleMessages),
        lastUserWebSearch: getWebSearchFromMessages(visibleMessages),
      };
    } catch (error) {
      console.error('Error loading chat:', error);
      // Reset the visible chat if this failed load is still the active one.
      if (
        loadChatRequestRef.current === requestId &&
        currentChatGuidRef.current === chatGuid
      ) {
        setMessages([]);
        updateCurrentChatGuid(null);
        updatePendingVisibleNewChat(false);
        activeChatMatterGuidRef.current = null;
      }
      // Propagate the error so it can be caught by the caller
      throw error;
    } finally {
      if (!options.silent) {
        setIsLoading(false);
      }
    }
  };

  
  // Helper function to extract document selections from the latest user message
  const getDocumentSelectionsFromMessages = (messages: Message[]): DocumentSelection[] => {
    // Find the latest user message with document_guids
    const userMessages = messages.filter(msg => msg.role === 'user');

    // If we have user messages, check only the latest one
    if (userMessages.length > 0) {
      const latestUserMsg = userMessages[userMessages.length - 1];

      // Check for document_guids on the message (stored in document_guids column)
      const docSelections = latestUserMsg.document_guids;

      if (docSelections && Array.isArray(docSelections) && docSelections.length > 0) {
        return docSelections as DocumentSelection[];
      }
    }

    return []; // No document selections found in the latest user message
  };

  const getWebSearchFromMessages = (chatMessages: Message[]): boolean => {
    const userMessages = chatMessages.filter((msg) => msg.role === 'user');
    if (userMessages.length === 0) return false;
    return userMessages[userMessages.length - 1]?.message_metadata?.web_search === true;
  };

  // Start a new chat
  const startNewChat = useCallback(() => {
    resetChatState();
  }, [resetChatState]);

  const persistStoppedGeneration = useCallback(async (target?: {
    chatGuid: string | null;
    generationId: string | null;
    lastEventId: string | null;
  }) => {
    // Callers that have already torn the stream down pass what they captured;
    // the refs are gone by then.
    const chatGuid = target ? target.chatGuid : streamingChatGuidRef.current;
    const generationId = target ? target.generationId : activeGenerationIdRef.current;
    const lastEventId = target ? target.lastEventId : lastEventIdRef.current;

    if (!selectedCase || !chatGuid || !generationId || hasPersistedStopRef.current) {
      return;
    }

    hasPersistedStopRef.current = true;
    try {
      // The cut is last_event_id: the server saves the turn as it stood then,
      // so the worker's wind-down cannot add anything the lawyer never saw.
      // keepalive so a refresh immediately after Stop still delivers that POST;
      // Axios is aborted on unload.
      const params = new URLSearchParams({ matter_guid: selectedCase });
      const response = await authFetch(
        `/api/case-partner/${chatGuid}/generations/${generationId}/stop?${params.toString()}`,
        {
          method: 'POST',
          keepalive: true,
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ last_event_id: lastEventId }),
        },
      );
      if (!response.ok) {
        throw new Error(
          await errorMessageFromResponse(response, STREAM_REQUEST_FAILED_MESSAGE),
        );
      }
      const payload = await response.json().catch(() => null);
      const status = payload?.status;
      if (typeof status === 'string' && !isActiveGenerationStatus(status)) {
        clearStopLatch(generationId);
      }
      return typeof status === 'string' ? status : undefined;
    } catch (error) {
      hasPersistedStopRef.current = false;
      throw error;
    }
  }, [selectedCase]);

  // Send a message to the AI
  const sendMessage = async (
    content: string,
    documentSelections: BackendDocumentSelection[] = [],
    options: SendMessageOptions = {}
  ) => {
    let startSettled = false;
    const settleStart = (result: SendMessageStartResult = {}) => {
      if (startSettled) return;
      startSettled = true;
      options.onStartSettled?.(result);
    };

    const streamAlreadyActive = sendInFlightRef.current || isStreaming;
    const shouldForceNewChat = Boolean(options.forceNewChat);
    const resumeGenerationId = options.resumeGenerationId;
    const pendingStop = readStopLatch(resumeGenerationId);
    const resumeAsStopped = generationResumeShouldLookStopped(
      options.resumeGenerationStatus,
      Boolean(pendingStop),
    );

    if (!selectedCase || (!resumeGenerationId && !content.trim())) {
      settleStart({
        error: selectedCase ? undefined : STREAM_REQUEST_FAILED_MESSAGE,
      });
      return;
    }
    if (
      !resumeGenerationId &&
      !shouldForceNewChat &&
      isActiveGenerationStatus(
        chatHistory.find((chat) => chat.guid === currentChatGuidRef.current)?.activeGenerationStatus
      ) &&
      !streamAlreadyActive
    ) {
      console.warn('Dropped Case Partner send while the current turn is still active');
      settleStart({ conflict: true });
      return { conflict: true };
    }
    const alreadyAttachedToResume = Boolean(
      resumeGenerationId &&
      streamAlreadyActive &&
      resumeGenerationId === activeGenerationIdRef.current &&
      streamingChatGuidRef.current === currentChatGuidRef.current
    );
    if (alreadyAttachedToResume) {
      settleStart();
      return;
    }
    const isSameVisibleChat = (
      !shouldForceNewChat &&
      streamingChatGuidRef.current !== null &&
      streamingChatGuidRef.current === currentChatGuidRef.current
    );
    if (streamAlreadyActive && isSameVisibleChat && !resumeGenerationId) {
      console.warn('Dropped duplicate Case Partner send while a request is already in flight');
      settleStart();
      return;
    }
    if (streamAlreadyActive) {
      detachRequestedRef.current = true;
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
    }

    sendInFlightRef.current = true;

    const streamGeneration = streamGenerationRef.current + 1;
    streamGenerationRef.current = streamGeneration;
    if (!resumeGenerationId) {
      loadChatRequestRef.current += 1;
    }
    const isActiveStream = () => streamGenerationRef.current === streamGeneration;
    let activeRequestChatGuid: string | null = null;
    let reloadChatAfterConflict: string | null = null;
    let claimedGenerationId: string | null = null;
    const isActiveRequestVisible = () => (
      isActiveStream() &&
      activeRequestChatGuid !== null &&
      currentChatGuidRef.current === activeRequestChatGuid
    );

    if (shouldForceNewChat) {
      setMessages([]);
      updateCurrentChatGuid(null);
      updateStreamingChatGuid(null);
      updatePendingVisibleNewChat(false);
      setStreamingContent('');
      setStreamingQuickReplyQuestions([]);
      setStreamingTemplateChoices(null);
      setStreamingThinkingSteps([]);
      setStreamingTextInsertions([]);
      setStreamingWaitMarkers([]);
      setFinalAnswerConfirmed(false);
      turnWorkedDurationMsRef.current = null;
      setTurnWorkedDurationMs(null);
      setIsThinking(false);
      activeChatMatterGuidRef.current = null;
      finalThinkingStepsRef.current = [];
      streamingWaitMarkersRef.current = [];
      liveStopSealedRef.current = false;
      hasReceivedFirstOutput.current = false;
      bundleMetadataRef.current = null;
      deadlineMetadataRef.current = null;
      notifiedUiChangeRef.current = new Set();
      draftDocumentMetadataRef.current = null;
      setLatestProducedDraftDocument(null);
    }
    
    // Get the user's timezone
    const timezone = getUserTimezone();
    const currentVisibleChatGuid = currentChatGuidRef.current;
    const canContinueCurrentMatterChat = (
      !shouldForceNewChat &&
      activeChatMatterGuidRef.current === selectedCase &&
      Boolean(currentVisibleChatGuid)
    );

    if (!resumeGenerationId) {
      const userMessageMetadata = {
        ...(options.userMessageMetadata || {}),
        ...(options.webSearch ? { web_search: true } : {}),
      };
      const userMessage: Message = {
        id: Date.now(),
        content,
        role: 'user',
        timestamp: toZonedTime(new Date(), timezone),
        message_metadata: Object.keys(userMessageMetadata).length > 0
          ? userMessageMetadata
          : undefined,
      };

      if (!canContinueCurrentMatterChat) {
        activeChatMatterGuidRef.current = selectedCase;
        updateCurrentChatGuid(null);
      }

      setMessages(prev => canContinueCurrentMatterChat ? [...prev, userMessage] : [userMessage]);
      updatePendingVisibleNewChat(!canContinueCurrentMatterChat);
    } else {
      updatePendingVisibleNewChat(false);
    }
    
    // Clear previous streaming data when starting a new message
    setStreamingThinkingSteps([]);
    setStreamingTextInsertions([]);
    setStreamingWaitMarkers([]);
    setFinalAnswerConfirmed(false);
    turnWorkedDurationMsRef.current = null;
    setTurnWorkedDurationMs(null);
    finalThinkingStepsRef.current = [];
    streamingWaitMarkersRef.current = [];
    liveStopSealedRef.current = false;
    accumulatedResponseRef.current = '';
    hasPersistedStopRef.current = false;
    hasReceivedFirstOutput.current = false;
    updateStopRequested(resumeAsStopped);
    if (resumeAsStopped) {
      canAbortStreamingRef.current = false;
      setCanAbortStreaming(false);
    }
    bundleMetadataRef.current = null;
    deadlineMetadataRef.current = null;
    notifiedUiChangeRef.current = new Set();
    draftDocumentMetadataRef.current = null;
    
    // Show the reasoning surface immediately
    setIsThinking(true);
    
    setIsStreaming(true);
    updateStreamCatchingUp(Boolean(options.resumeGenerationId));
    clearCatchUpAfterPaintRef.current = false;
    if (!resumeAsStopped) {
      canAbortStreamingRef.current = true;
      setCanAbortStreaming(true);
    }
    updateStreamingChatGuid(canContinueCurrentMatterChat ? currentVisibleChatGuid : null);
    setStreamingContent('');
    setStreamingQuickReplyQuestions([]);
    setStreamingTemplateChoices(null);
    
    // Create abort controller for cancellation
    abortControllerRef.current = new AbortController();

    // The turn's clock, not this connection's: when we are picking a turn back
    // up, the server tells us when it began, so leaving the chat and returning
    // reports the whole run rather than restarting from the visit.
    const turnStartedAt = isoToEpochMs(options.resumeStartedAt) ?? Date.now();
    turnStartedAtRef.current = turnStartedAt;
    turnWorkedDurationMsRef.current = null;
    setTurnStartedAt(turnStartedAt);
    setTurnWorkedDurationMs(null);
    let pendingStreamingContent = '';
    let contentUpdateTimeout: ReturnType<typeof setTimeout> | null = null;
    let contentUpdateFrame: number | null = null;
    let lastContentFlushAt = 0;
    let timelineInsertions: TimelineTextInsertion[] = [];

    const clearScheduledStreamingContentUpdate = () => {
      if (contentUpdateTimeout !== null) {
        clearTimeout(contentUpdateTimeout);
        contentUpdateTimeout = null;
      }

      if (contentUpdateFrame !== null && typeof cancelAnimationFrame === 'function') {
        cancelAnimationFrame(contentUpdateFrame);
        contentUpdateFrame = null;
      }
    };

    const commitStreamingContent = () => {
      lastContentFlushAt = getCurrentTime();
      setStreamingContent(pendingStreamingContent);
      setStreamingTextInsertions(timelineInsertions);
    };

    const queueStreamingContentCommit = () => {
      if (typeof requestAnimationFrame === 'function') {
        contentUpdateFrame = requestAnimationFrame(() => {
          contentUpdateFrame = null;
          commitStreamingContent();
        });
        return;
      }

      contentUpdateTimeout = setTimeout(() => {
        contentUpdateTimeout = null;
        commitStreamingContent();
      }, 0);
    };

    const scheduleStreamingContentUpdate = (nextContent: string) => {
      pendingStreamingContent = nextContent;

      if (contentUpdateTimeout !== null || contentUpdateFrame !== null) {
        return;
      }

      const elapsed = getCurrentTime() - lastContentFlushAt;
      const delay = Math.max(0, STREAM_CONTENT_UPDATE_INTERVAL_MS - elapsed);

      if (delay > 0) {
        contentUpdateTimeout = setTimeout(() => {
          contentUpdateTimeout = null;
          queueStreamingContentCommit();
        }, delay);
        return;
      }

      queueStreamingContentCommit();
    };

    const flushStreamingContentNow = () => {
      clearScheduledStreamingContentUpdate();
      setStreamingContent(pendingStreamingContent);
      setStreamingTextInsertions(timelineInsertions);
      lastContentFlushAt = getCurrentTime();
    };

    let accumulatedResponse = '';
    let accumulatedDisplayResponse = '';
    let turnState: TurnReducerState = emptyTurnReducerState();

    const publishStreamingPresentation = (content: string) => {
      const streamingPresentation = extractStreamingAssistantPresentation(content);
      accumulatedDisplayResponse = streamingPresentation.content;
      setStreamingQuickReplyQuestions(streamingPresentation.quickReplyQuestions);
      setStreamingTemplateChoices(streamingPresentation.templateChoices);
      scheduleStreamingContentUpdate(accumulatedDisplayResponse);
    };

    const flushCatchUpPresentation = () => {
      publishStreamingPresentation(accumulatedResponse);
      flushStreamingContentNow();
    };

    const syncAccumulatedResponse = (nextText: string) => {
      const textChanged = nextText !== accumulatedResponse;
      accumulatedResponse = nextText;
      accumulatedResponseRef.current = nextText;
      return textChanged;
    };

    const syncTurnPresentation = (
      state: TurnReducerState,
      options?: { replay?: boolean; publish?: boolean },
    ) => {
      const previousText = accumulatedResponse;
      const textChanged = syncAccumulatedResponse(state.content);
      const steps = thinkingStepsFromSnapshot(state.thinking_steps) as ThinkingStep[];
      finalThinkingStepsRef.current = steps;
      setStreamingThinkingSteps(steps);
      const answerConfirmed = Boolean(state.final_answer_confirmed);
      setFinalAnswerConfirmed(answerConfirmed);
      const durationMs = positiveDurationMs(state.reasoning_duration_ms);
      if (durationMs != null) {
        turnWorkedDurationMsRef.current = durationMs;
        setTurnWorkedDurationMs(durationMs);
      } else if (!answerConfirmed) {
        turnWorkedDurationMsRef.current = null;
        setTurnWorkedDurationMs(null);
      }
      const waitMarkers = state.lead_wait_markers ?? [];
      streamingWaitMarkersRef.current = waitMarkers;
      setStreamingWaitMarkers(waitMarkers);

      if (
        options?.replay
        || previousText === ''
        || !accumulatedResponse.startsWith(previousText)
      ) {
        timelineInsertions = timelineInsertionsFromTurnState(
          state.content,
          steps,
          state.round_start,
          state.current_round,
          answerConfirmed,
          state.text_segments,
        );
      } else if (textChanged) {
        timelineInsertions = appendStreamedTimelineText({
          insertions: timelineInsertions,
          steps,
          delta: accumulatedResponse.slice(previousText.length),
          currentRound: state.current_round,
          roundStart: state.round_start,
          finalAnswerConfirmed: answerConfirmed,
          messageId: state.text_segments?.[state.text_segments.length - 1]?.message_id,
        });
      }
      setStreamingTextInsertions(timelineInsertions);
      if (state.bundle?.bundle_url) {
        bundleMetadataRef.current = {
          bundle_url: state.bundle.bundle_url,
          bundle_title: state.bundle.bundle_title || 'Bundle',
        };
      }
      if (state.deadline?.deadline_url) {
        deadlineMetadataRef.current = {
          deadline_url: state.deadline.deadline_url,
          deadline_title: state.deadline.deadline_title || 'Deadline',
        };
      }
      // Snapshot-based reconnects don't replay tool_executed events, so trigger open-screen
      // refreshes from the reconstructed turn state instead (once per changed resource).
      for (const change of uiChangesFromTurn(state)) {
        const key = uiChangeKey(change.kind, change.id);
        if (notifiedUiChangeRef.current.has(key)) continue;
        notifiedUiChangeRef.current.add(key);
        notifyUiChanged(change.kind, change.id);
      }
      if (state.draft_metadata) {
        draftDocumentMetadataRef.current = state.draft_metadata;
      }
      if (textChanged && options?.publish !== false && !options?.replay) {
        publishStreamingPresentation(accumulatedResponse);
      }
      return textChanged;
    };

    const getToolMessageMetadata = () => ({
      ...(bundleMetadataRef.current || {}),
      ...(deadlineMetadataRef.current || {}),
      ...(draftDocumentMetadataRef.current || {}),
    });

    const getReasoningDurationMetadata = (steps?: ThinkingStep[]) => (
      reasoningDurationMetadata({
        hasSteps: Boolean(steps && steps.length > 0),
        stateDurationMs: turnState.reasoning_duration_ms,
        frozenDurationMs: turnWorkedDurationMsRef.current,
        turnStartedAt: turnStartedAtRef.current,
      })
    );

    const getLeadWaitDurationMetadata = () => (
      turnState.lead_waited_duration_ms
        ? {
            lead_waited_duration_ms: turnState.lead_waited_duration_ms,
            lead_wait_markers: turnState.lead_wait_markers ?? [],
          }
        : {}
    );

    const appendStreamIssueMessage = (errorMessage: string) => {
      if (!isActiveRequestVisible()) return;

      const timezone = getUserTimezone();
      const finalSteps = finalStepsForEndedTurn(finalThinkingStepsRef.current);

      if (accumulatedResponse) {
        const interruptedPresentation = buildInterruptedAssistantMessagePresentation(
          accumulatedResponse,
          errorMessage,
        );

        flushStreamingContentNow();
        setMessages(prev => [
          ...prev,
          {
            id: Date.now(),
            content: interruptedPresentation.content,
            role: 'assistant',
            timestamp: toZonedTime(new Date(), timezone),
            thinkingSteps: finalSteps,
            waitMarkers: closeOpenWaitMarkers(turnState.lead_wait_markers),
            timelineInsertions: timelineInsertions.length > 0
              ? addRoundTimelineInsertion(
                  timelineInsertions,
                  finalSteps?.length ?? 0,
                  [],
                  errorMessage,
                )
              : undefined,
            message_metadata: {
              ...getToolMessageMetadata(),
              ...getReasoningDurationMetadata(finalSteps),
              ...getLeadWaitDurationMetadata(),
              ...(interruptedPresentation.metadata || {}),
              stream_interrupted: true,
              stream_error: errorMessage,
            },
          }
        ]);
        return;
      }

      setMessages(prev => [
        ...prev,
        {
          id: Date.now(),
          content: errorMessage,
          role: 'assistant',
          timestamp: toZonedTime(new Date(), timezone),
          thinkingSteps: finalSteps,
          waitMarkers: closeOpenWaitMarkers(turnState.lead_wait_markers),
          message_metadata: {
            ...getToolMessageMetadata(),
            ...getReasoningDurationMetadata(finalSteps),
            ...getLeadWaitDurationMetadata(),
            stream_interrupted: true,
            stream_error: errorMessage,
          },
        }
      ]);
    };

    const appendStoppedAssistantMessage = () => {
      const finalSteps = finalStepsForEndedTurn(finalThinkingStepsRef.current);
      if ((!accumulatedResponse && !finalSteps) || !isActiveRequestVisible()) {
        return;
      }

      flushStreamingContentNow();
      const timezone = getUserTimezone();
      const generationId = resumeGenerationId || activeGenerationIdRef.current;
      const stoppedPresentation = buildStoppedAssistantMessagePresentation(accumulatedResponse || '', {
        ...getToolMessageMetadata(),
        ...getReasoningDurationMetadata(finalSteps),
        ...getLeadWaitDurationMetadata(),
        stopped_by_user: stopRequestedRef.current,
        ...(generationId ? { generation_id: generationId } : {}),
      });
      if (!stoppedPresentation) return;

      setMessages((prev) => {
        if (transcriptHasAssistantForGeneration(prev, generationId)) {
          return prev;
        }
        return [
          ...prev,
          {
            id: Date.now(),
            content: stoppedPresentation.content,
            role: 'assistant',
            timestamp: toZonedTime(new Date(), timezone),
            thinkingSteps: finalSteps,
            waitMarkers: closeOpenWaitMarkers(turnState.lead_wait_markers),
            timelineInsertions: timelineInsertions.length > 0 ? [...timelineInsertions] : undefined,
            message_metadata: stoppedPresentation.metadata,
            draftCardsDefaultExpanded: shouldDefaultExpandDraftCards(stoppedPresentation.metadata),
          },
        ];
      });
    };
    
    try {
      let chatGuid = canContinueCurrentMatterChat ? currentVisibleChatGuid : null;
      if (resumeGenerationId) {
        chatGuid = currentVisibleChatGuid;
        if (!chatGuid) {
          settleStart({ error: STREAM_REQUEST_FAILED_MESSAGE });
          return;
        }
      }

      const requestId = resumeGenerationId || (
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(16).slice(2)}`
      );
      claimedGenerationId = requestId;
      const resumeSameGeneration = (
        Boolean(resumeGenerationId) &&
        resumeGenerationId === activeGenerationIdRef.current
      );
      if (!resumeSameGeneration) {
        lastEventIdRef.current = null;
      }
      activeGenerationIdRef.current = requestId;
      detachRequestedRef.current = false;

      const params = new URLSearchParams();
      params.append('matter_guid', selectedCase);

      if (!resumeGenerationId) {
        const startResponse = await authFetch(
          `/api/case-partner/stream?${params.toString()}&requestId=${requestId}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              message: content,
              ...(chatGuid ? { chat_guid: chatGuid } : {}),
              generation_id: requestId,
              matter_guid: selectedCase,
              document_selections: documentSelections.length > 0 ? documentSelections : undefined,
              user_message_metadata: options.userMessageMetadata,
              ui_context: options.uiContext,
              web_search: Boolean(options.webSearch),
            }),
          },
        );

        if (startResponse.status === 409) {
          reloadChatAfterConflict = chatGuid;
          settleStart({ conflict: true });
          return { conflict: true };
        }
        if (!startResponse.ok) {
          throw new Error(
            await errorMessageFromResponse(
              startResponse,
              STREAM_REQUEST_FAILED_MESSAGE,
            ),
          );
        }
        const startBody = await startResponse.json().catch(() => null);
        const startedChatGuid = typeof startBody?.chat_guid === 'string'
          ? startBody.chat_guid
          : null;
        if (!chatGuid) {
          if (!startedChatGuid) {
            throw new Error(STREAM_REQUEST_FAILED_MESSAGE);
          }
          chatGuid = startedChatGuid;
        }
      }

      if (!chatGuid) {
        settleStart({ error: STREAM_REQUEST_FAILED_MESSAGE });
        return;
      }

      activeRequestChatGuid = chatGuid;
      settleStart({ started: true, chatGuid });
      const stillOnThisTurn = isActiveStream() && (
        currentChatGuidRef.current === chatGuid ||
        isPendingVisibleNewChatRef.current
      );
      if (stillOnThisTurn) {
        if (!canContinueCurrentMatterChat && isPendingVisibleNewChatRef.current) {
          updateCurrentChatGuid(chatGuid);
          activeChatMatterGuidRef.current = selectedCase;
          updateStreamingChatGuid(chatGuid);
          updatePendingVisibleNewChat(false);
        } else {
          updateStreamingChatGuid(chatGuid);
        }
      }

      // Superseded turns must not claim stream refs after start is accepted.
      if (!isActiveStream()) {
        void loadChatHistory(false);
        return;
      }

      // Only mark the chat active after start is accepted (or we are resuming).
      if (isActiveStream()) {
        const nextActive: {
          activeGenerationId: string;
          activeGenerationStatus: 'stopping' | 'generating';
          updatedAt: Date;
        } = {
          activeGenerationId: requestId,
          activeGenerationStatus: resumeAsStopped ? 'stopping' : 'generating',
          updatedAt: new Date(),
        };
        const promoteChat = (prev: ChatSession[]) => {
          const existing = prev.find((chat) => chat.guid === chatGuid);
          if (existing) {
            return [
              { ...existing, ...nextActive },
              ...prev.filter((chat) => chat.guid !== chatGuid),
            ];
          }
          return prev;
        };
        setChatHistory((prev) => {
          const promoted = promoteChat(prev);
          if (promoted !== prev) return promoted;
          const fallbackTitle = content.trim();
          return [
            {
              guid: chatGuid,
              title: fallbackTitle.length > 40
                ? `${fallbackTitle.slice(0, 37)}...`
                : (fallbackTitle || 'New Chat'),
              messages: [],
              createdAt: nextActive.updatedAt,
              ...nextActive,
            },
            ...prev,
          ];
        });
        if (chatHistorySearchQueryRef.current.trim()) {
          setChatHistorySearchResults(promoteChat);
        }
      }

      if (stopRequestedRef.current) {
        void persistStoppedGeneration({
          chatGuid,
          generationId: requestId,
          lastEventId: pendingStop?.lastEventId ?? lastEventIdRef.current,
        }).catch((error) => {
          console.error('Error requesting Case Partner stop:', error);
        });
      }
      const shouldAttach = (
        isActiveStream() &&
        currentChatGuidRef.current === chatGuid &&
        !detachRequestedRef.current
      );
      if (!shouldAttach) {
        if (activeGenerationIdRef.current === requestId && !isActiveStream()) {
          activeGenerationIdRef.current = null;
        }
        void loadChatHistory(false);
        return;
      }

      // A dropped SSE connection is not a terminal generation. Re-attach with
      // last_event_id before tearing down this turn so incremental resume can run.
      let streamError: string | null = null;
      let receivedCompleteEvent = false;
      let receivedStoppedEvent = false;
      let consecutiveDropFailures = 0;

      while (true) {
        let receivedEventThisAttempt = false;
        let attemptDropped = false;
        let streamReader: ReadableStreamDefaultReader<Uint8Array> | null = null;
        // Reopen/reconnect only. A live attach also replays the gap between
        // POST and SSE; treating that as catch-up snapped the answer instead
        // of typing it.
        const attachIsCatchUp = Boolean(resumeGenerationId) || Boolean(lastEventIdRef.current);
        if (attachIsCatchUp) {
          updateStreamCatchingUp(true);
          clearCatchUpAfterPaintRef.current = false;
        }

        try {
          const abortController: AbortController = (
            abortControllerRef.current && !abortControllerRef.current.signal.aborted
              ? abortControllerRef.current
              : new AbortController()
          );
          abortControllerRef.current = abortController;

          const attachParams = new URLSearchParams();
          attachParams.append('matter_guid', selectedCase);
          if (lastEventIdRef.current) {
            attachParams.append('last_event_id', lastEventIdRef.current);
          }

          const response = await authFetch(
            `/api/case-partner/${chatGuid}/generations/${requestId}/stream?${attachParams.toString()}`,
            {
              method: 'GET',
              headers: {
                Accept: 'text/event-stream',
              },
              signal: abortController.signal,
            },
          );

          if (!response.ok || !response.body) {
            console.error('Case Partner stream attach failed', response.status);
            const attachError = new Error(STREAM_REQUEST_FAILED_MESSAGE) as Error & { status?: number };
            attachError.status = response.status;
            throw attachError;
          }

          const reader = response.body.getReader();
          streamReader = reader;
          const decoder = new TextDecoder();
          let done = false;
          let buffer = '';

          while (!done) {
        const { value, done: readerDone } = await reader.read();
        if (readerDone) {
          if (!receivedCompleteEvent && !receivedStoppedEvent && !streamError) {
            attemptDropped = true;
          }
          done = true;
          break;
        }
        
        // Decode the chunk and add it to our buffer
        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;
        
        // Process complete SSE events (events are separated by double newlines)
        const events = buffer.split('\n\n');
        // Keep the last (potentially incomplete) event in the buffer
        buffer = events.pop() || '';
        
        // Process each complete event
        for (const event of events) {
          if (event.trim() && event.startsWith('data: ')) {
            try {
              // Extract the JSON data from the event
              const jsonStr = event.replace('data: ', '').trim();
              const data = JSON.parse(jsonStr);
              if (liveStopSealedRef.current) {
                // The click already sealed the tape. Do not fold later events
                // back into it, but still honour a terminal so a clean socket
                // close cannot look like an interrupted stream.
                const sealedTerminal = streamEventTerminalKind(data);
                if (sealedTerminal) {
                  notifyTurnUiChanges(turnState);
                  receivedStoppedEvent = true;
                  done = true;
                  break;
                }
                continue;
              }
              receivedEventThisAttempt = true;
              if (typeof data.event_id === 'string') {
                lastEventIdRef.current = data.event_id;
              }
              if (attachIsCatchUp && (data.type === 'snapshot' || data.replay === true)) {
                updateStreamCatchingUp(true);
              } else if (
                data.type !== 'ping'
                && data.type !== 'replay_complete'
              ) {
                if (streamCatchingUpRef.current) {
                  clearCatchUpAfterPaintRef.current = true;
                }
              }
              if (data.type === 'replay_complete') {
                flushCatchUpPresentation();
                continue;
              }
              if (data.type === 'snapshot' && typeof data.content === 'string') {
                if (typeof data.last_event_id === 'string') {
                  lastEventIdRef.current = data.last_event_id;
                }
                turnState = turnReducerFromSnapshot(data);
                syncTurnPresentation(turnState, { publish: false });
                if (turnState.thinking_steps.length > 0 || turnState.content) {
                  hasReceivedFirstOutput.current = true;
                }
                if (turnState.thinking_steps.length > 0) {
                  setIsThinking(true);
                }
                flushCatchUpPresentation();
                continue;
              }
              if (
                data.type !== 'ping' &&
                isActiveStream() &&
                !stopRequestedRef.current &&
                !canAbortStreamingRef.current
              ) {
                canAbortStreamingRef.current = true;
                setCanAbortStreaming(true);
              }

              const terminalKind = streamEventTerminalKind(data);
              if (terminalKind === 'error') {
                streamError = typeof data.error === 'string' && data.error
                  ? data.error
                  : STREAM_INTERRUPTED_MESSAGE;
                finishedGenerationIdRef.current = activeGenerationIdRef.current;
                clearStopLatch(activeGenerationIdRef.current);
                if (activeRequestChatGuid) {
                  setChatHistory((prev) => prev.map((chat) => (
                    chat.guid === activeRequestChatGuid
                      ? { ...chat, activeGenerationId: null, activeGenerationStatus: null }
                      : chat
                  )));
                }
                done = true;
                break;
              }

              const nextTurnState = applyTurnEvent(turnState, data);
              if (nextTurnState !== turnState) {
                turnState = nextTurnState;
                if (turnState.thinking_steps.length > 0 || turnState.content) {
                  hasReceivedFirstOutput.current = true;
                }
                if (
                  data.tool_call_started
                  || data.reasoning_part_added
                  || data.reasoning
                  || data.reasoning_complete
                ) {
                  setIsThinking(true);
                }
                if (data.type === 'final_answer_confirmed' || data.final_answer_confirmed) {
                  flushSync(() => {
                    syncTurnPresentation(turnState, { replay: data.replay });
                  });
                  if (!data.replay) {
                    await waitForBrowserPaint();
                  }
                } else if (data.reasoning_complete) {
                  flushSync(() => {
                    syncTurnPresentation(turnState, { replay: data.replay });
                  });
                  if (!data.replay) {
                    await waitForBrowserPaint();
                  }
                } else {
                  syncTurnPresentation(turnState, { replay: data.replay });
                }
              }

              if (data.tool_executed && data.round_num !== undefined) {
                for (const change of normalizeUiChanges(data.metadata)) {
                  const key = uiChangeKey(change.kind, change.id);
                  if (notifiedUiChangeRef.current.has(key)) continue;
                  notifiedUiChangeRef.current.add(key);
                  notifyUiChanged(change.kind, change.id);
                }
                if (
                  data.metadata?.draft_document_action !== 'inspect'
                  && (data.metadata?.draft_document_guid || data.metadata?.draft_document_job_guid)
                ) {
                  const row = draftMetadataRow(data.metadata);
                  if (row?.draft_document_guid) {
                    setLatestProducedDraftDocument({
                      documentGuid: String(row.draft_document_guid),
                      title: typeof row.draft_document_title === 'string' ? row.draft_document_title : undefined,
                      version: typeof row.draft_document_version === 'number' ? row.draft_document_version : undefined,
                      action: 'create',
                      chatGuid: activeRequestChatGuid,
                      generationId: requestId,
                      producedAt: Date.now(),
                    });
                  }
                }
              }
              
              // If this is the final chunk, we can break
              if (terminalKind === 'stopped' || terminalKind === 'complete') {
                // Tool-exec notifies can race a still-uncommitted write. The
                // turn is durable now, so tell open bundle screens again.
                notifyTurnUiChanges(turnState);
              }
              if (terminalKind === 'stopped') {
                receivedStoppedEvent = true;
                finishedGenerationIdRef.current = activeGenerationIdRef.current;
                clearStopLatch(activeGenerationIdRef.current);
                if (activeRequestChatGuid) {
                  setChatHistory((prev) => prev.map((chat) => (
                    chat.guid === activeRequestChatGuid
                      ? { ...chat, activeGenerationId: null, activeGenerationStatus: null }
                      : chat
                  )));
                }
                done = true;
              } else if (terminalKind === 'complete') {
                receivedCompleteEvent = true;
                finishedGenerationIdRef.current = activeGenerationIdRef.current;
                clearStopLatch(activeGenerationIdRef.current);
                if (activeRequestChatGuid) {
                  setChatHistory((prev) => prev.map((chat) => (
                    chat.guid === activeRequestChatGuid
                      ? { ...chat, activeGenerationId: null, activeGenerationStatus: null }
                      : chat
                  )));
                }
                done = true;
              }
            } catch (e) {
              console.error('Error parsing SSE event:', e, event);
            }
          }
        }
      }

          attemptDropped = shouldRetryDroppedGenerationStream({
            streamError,
            receivedCompleteEvent,
            receivedStoppedEvent,
            detachRequested: detachRequestedRef.current,
            isActiveStream: isActiveStream(),
            stopRequested: stopRequestedRef.current,
          });
        } catch (attachError) {
          if (
            !isRetryableGenerationStreamFailure(attachError) ||
            !shouldRetryDroppedGenerationStream({
              streamError: null,
              receivedCompleteEvent,
              receivedStoppedEvent,
              detachRequested: detachRequestedRef.current,
              isActiveStream: isActiveStream(),
              stopRequested: stopRequestedRef.current,
            })
          ) {
            throw attachError;
          }
          attemptDropped = true;
        } finally {
          if (streamReader) {
            try {
              await streamReader.cancel();
            } catch {
              // Already closed by the server, abort, or a previous cancel.
            }
          }
        }

        if (receivedEventThisAttempt) {
          consecutiveDropFailures = 0;
        }

        if (!attemptDropped) {
          break;
        }

        const delayMs = streamReconnectDelayMs(consecutiveDropFailures);
        if (delayMs === null) {
          streamError = STREAM_INTERRUPTED_MESSAGE;
          break;
        }
        consecutiveDropFailures += 1;
        if (
          !isActiveStream() ||
          detachRequestedRef.current ||
          abortControllerRef.current?.signal.aborted
        ) {
          throw abortError();
        }
        await waitForAbortableDelay(delayMs, abortControllerRef.current?.signal);
      }

      if (stopRequestedRef.current) {
        appendStoppedAssistantMessage();
        return;
      }

      const controlledStreamError = streamError || (
        receivedCompleteEvent || receivedStoppedEvent || detachRequestedRef.current
          ? null
          : STREAM_INTERRUPTED_MESSAGE
      );
      if (controlledStreamError) {
        appendStreamIssueMessage(controlledStreamError);
        return;
      }

      if (receivedStoppedEvent) {
        appendStoppedAssistantMessage();
        return;
      }

      if (!isActiveRequestVisible()) {
        return;
      }

      flushStreamingContentNow();

      // Add the complete assistant message to our messages
      if (isActiveRequestVisible()) {
        // Get the user's timezone
        const timezone = getUserTimezone();

        // Use the ref to get the final thinking steps
        const finalSteps = finalThinkingStepsRef.current.length > 0
          ? [...finalThinkingStepsRef.current]
          : undefined;
        const assistantMarkerResult = extractAssistantMarkers(accumulatedResponse);
        const generationId = resumeGenerationId || activeGenerationIdRef.current || requestId;
        const assistantMetadata = {
          ...getToolMessageMetadata(),
          ...getReasoningDurationMetadata(finalSteps),
          ...getLeadWaitDurationMetadata(),
          ...(assistantMarkerResult.metadata || {}),
          ...(generationId ? { generation_id: generationId } : {}),
        };
        if (!assistantMarkerResult.content && Object.keys(assistantMetadata).length === 0) {
          return;
        }

        const assistantMessage: Message = {
          id: Date.now(), // Add a temporary unique ID using timestamp
          content: assistantMarkerResult.content,
          role: 'assistant',
          timestamp: toZonedTime(new Date(), timezone), // Use the user's timezone
          thinkingSteps: finalSteps,
          waitMarkers: closeOpenWaitMarkers(turnState.lead_wait_markers),
          timelineInsertions: timelineInsertions.length > 0 ? [...timelineInsertions] : undefined,
          message_metadata: Object.keys(assistantMetadata).length > 0 ? assistantMetadata : undefined,
          draftCardsDefaultExpanded: shouldDefaultExpandDraftCards(assistantMetadata),
        };

        setMessages((prev) => {
          if (transcriptHasAssistantForGeneration(prev, requestId)) {
            return prev;
          }
          return [...prev, assistantMessage];
        });
      }
      
    } catch (error: any) {
      if (error?.name === 'AbortError' || stopRequestedRef.current) {
        // Stopping aborts this stream deliberately, and that abort is the end
        // of the turn: finalize what was rendered rather than waiting for the
        // worker's terminal event. Other aborts are chat switches, where the
        // turn keeps running and must not be written down. A reconnect that
        // already knows the lawyer stopped (refresh during `stopping`) also
        // ends here if the live socket drops — the cut is the turn.
        if (stopRequestedRef.current) {
          appendStoppedAssistantMessage();
        }
        settleStart({ error: STREAM_REQUEST_FAILED_MESSAGE });
        return;
      }
      const errorMessage = error?.message || STREAM_REQUEST_FAILED_MESSAGE;
      settleStart({ error: errorMessage });
      console.error('Error in chat stream:', errorMessage);
      // Retryable failures should auto-resume while the server turn is still live.
      // Non-retryable attach/start failures must latch finished or history polling
      // will restart attach forever.
      if (claimedGenerationId && shouldLatchFinishedGenerationOnStreamFailure(error)) {
        finishedGenerationIdRef.current = claimedGenerationId;
      }
      if (activeRequestChatGuid && claimedGenerationId) {
        const failedChatGuid = activeRequestChatGuid;
        const failedGenerationId = claimedGenerationId;
        setChatHistory((prev) => prev.map((chat) => (
          chat.guid === failedChatGuid && chat.activeGenerationId === failedGenerationId
            ? { ...chat, activeGenerationId: null, activeGenerationStatus: null }
            : chat
        )));
      }
      appendStreamIssueMessage(errorMessage);
    } finally {
      settleStart();
      clearScheduledStreamingContentUpdate();
      if (isActiveStream()) {
        sendInFlightRef.current = false;
        if (activeRequestChatGuid) {
          void loadChatHistory(false);
        }

        setIsStreaming(false);
        updateStreamCatchingUp(false);
        canAbortStreamingRef.current = false;
        setCanAbortStreaming(false);
        setStreamingContent('');
        setStreamingQuickReplyQuestions([]);
        setStreamingTemplateChoices(null);
        setIsThinking(false);
        updatePendingVisibleNewChat(false);
        // Clear streaming thinking steps after they've been added to the message
        setStreamingThinkingSteps([]);
        setStreamingTextInsertions([]);
        setStreamingWaitMarkers([]);
        setFinalAnswerConfirmed(false);
        finalThinkingStepsRef.current = [];
        streamingWaitMarkersRef.current = [];
        liveStopSealedRef.current = false;
        hasReceivedFirstOutput.current = false;
        bundleMetadataRef.current = null;
      deadlineMetadataRef.current = null;
        notifiedUiChangeRef.current = new Set();
        draftDocumentMetadataRef.current = null;
        updateStopRequested(false);
        abortControllerRef.current = null;
        activeGenerationIdRef.current = null;
        setTurnStartedAt(null);
        turnWorkedDurationMsRef.current = null;
        setTurnWorkedDurationMs(null);
        accumulatedResponseRef.current = '';
        updateStreamingChatGuid(null);
        if (reloadChatAfterConflict) {
          void loadChat(reloadChatAfterConflict, { force: true, silent: true });
        }
      }
    }
  };

  const sendMessageRef = useRef(sendMessage);
  sendMessageRef.current = sendMessage;

  const attachGeneration = (
    generationId: string,
    startedAt: string | null = null,
    status: string | null = null,
  ) => (
    sendMessageRef.current('', [], {
      resumeGenerationId: generationId,
      resumeStartedAt: startedAt,
      resumeGenerationStatus: status,
    })
  );

  useEffect(() => {
    if (isLoading || !currentChatGuid || sendInFlightRef.current) return;
    const chat = chatHistory.find((item) => item.guid === currentChatGuid);
    if (
      !chat?.activeGenerationId ||
      !isActiveGenerationStatus(chat.activeGenerationStatus) ||
      activeGenerationIdRef.current === chat.activeGenerationId ||
      finishedGenerationIdRef.current === chat.activeGenerationId
    ) {
      return;
    }
    void attachGeneration(
      chat.activeGenerationId,
      chat.activeGenerationStartedAt ?? null,
      chat.activeGenerationStatus ?? null,
    );
  }, [chatHistory, currentChatGuid, isLoading, isStreaming, messages]);

  useEffect(() => {
    const persistStopOnUnload = () => {
      if (!stopRequestedRef.current) return;
      const chatGuid = streamingChatGuidRef.current;
      const generationId = activeGenerationIdRef.current;
      if (!chatGuid || !generationId) return;
      void persistStoppedGeneration({
        chatGuid,
        generationId,
        lastEventId: lastEventIdRef.current,
      });
    };
    window.addEventListener('pagehide', persistStopOnUnload);
    return () => window.removeEventListener('pagehide', persistStopOnUnload);
  }, [persistStoppedGeneration]);

  // Abort streaming
  const abortStreaming = useCallback(async () => {
    if (!canAbortStreamingRef.current || stopRequestedRef.current) {
      return;
    }
    const chatGuid = streamingChatGuidRef.current;
    const generationId = activeGenerationIdRef.current;
    const lastEventId = lastEventIdRef.current;
    // Seal before `liveStreaming` flips off: spawn chips treat `{status:"running"}`
    // as in-flight only while live, and open wait items keep pulsing until closed.
    const liveSteps = finalThinkingStepsRef.current;
    const liveWaitMarkers = streamingWaitMarkersRef.current;
    const sealed = sealLiveTurnPresentation(liveSteps, liveWaitMarkers);
    finalThinkingStepsRef.current = sealed.steps;
    streamingWaitMarkersRef.current = sealed.waitMarkers;
    setStreamingThinkingSteps(sealed.steps);
    setStreamingWaitMarkers(sealed.waitMarkers);
    liveStopSealedRef.current = true;
    updateStopRequested(true);
    canAbortStreamingRef.current = false;
    setCanAbortStreaming(false);
    // End the turn here instead of waiting for the worker to notice, unwind its
    // agents and report back. Aborting before the first await means no further
    // event can land, so what is on screen is exactly the cut sent below and
    // both sides finalize the same turn.
    //
    // The generation stays `stopping` in the chat list while the worker winds
    // down, so it has to be latched finished too or the attach effect reopens
    // the stream we just ended.
    if (generationId) {
      finishedGenerationIdRef.current = generationId;
    }
    if (chatGuid && generationId) {
      // Written before the POST so a refresh can re-issue the stop if unload
      // cancelled the request.
      writeStopLatch({ chatGuid, generationId, lastEventId });
    }
    if (chatGuid) {
      // Hide the thinking dot immediately. Persist may still return `stopping`
      // while the worker winds down; that must not look like a live turn.
      setChatHistory((prev) => prev.map((chat) => (
        chat.guid === chatGuid
          ? { ...chat, activeGenerationStatus: 'stopping' }
          : chat
      )));
    }
    abortControllerRef.current?.abort();
    try {
      const status = await persistStoppedGeneration({ chatGuid, generationId, lastEventId });
      if (chatGuid) {
        setChatHistory((prev) => prev.map((chat) => {
          if (chat.guid !== chatGuid) return chat;
          if (isActiveGenerationStatus(status)) {
            return { ...chat, activeGenerationStatus: status };
          }
          return { ...chat, activeGenerationId: null, activeGenerationStatus: null };
        }));
      }
    } catch (error) {
      console.error('Error requesting Case Partner stop:', error);
      // The server never heard about the stop, so the turn is still running and
      // will finish on its own. Drop the message we optimistically ended and let
      // history polling re-attach to it.
      toast.error('Could not stop this turn. It may still be running.');
      if (generationId) {
        clearStopLatch(generationId);
        liveStopSealedRef.current = false;
        if (activeGenerationIdRef.current === generationId) {
          finalThinkingStepsRef.current = liveSteps;
          streamingWaitMarkersRef.current = liveWaitMarkers;
          setStreamingThinkingSteps(liveSteps);
          setStreamingWaitMarkers(liveWaitMarkers);
          updateStopRequested(false);
        }
        setMessages((prev) => withoutAssistantMessagesForGeneration(prev, generationId));
        if (finishedGenerationIdRef.current === generationId) {
          finishedGenerationIdRef.current = null;
        }
        if (chatGuid) {
          setChatHistory((prev) => prev.map((chat) => (
            chat.guid === chatGuid
              ? { ...chat, activeGenerationStatus: 'generating' }
              : chat
          )));
        }
      }
    }
  }, [persistStoppedGeneration, updateStopRequested]);

  const isStreamingCurrentChat = isStreaming && (
    (streamingChatGuid !== null && streamingChatGuid === currentChatGuid) ||
    (isPendingVisibleNewChat && streamingChatGuid === null && currentChatGuid === null)
  );
  const currentChatTurnActive = isCurrentChatTurnActive(
    isStreamingCurrentChat,
    currentChatGuid
      ? chatHistory.find((chat) => chat.guid === currentChatGuid)?.activeGenerationStatus
      : null,
  );

  // Initialize with empty state
  useEffect(() => {
    if (!currentChatGuid) {
      // No need to call startNewChat() as we want to show the initial UI
      // with the "How can I assist?" message in the component
    }
  }, [currentChatGuid]);

  const renameChat = async (chatGuid: string, title: string) => {
    if (!selectedCase || !chatGuid) return;

    const nextTitle = title.trim();
    if (!nextTitle) {
      throw new Error('Chat name cannot be empty');
    }
    if (nextTitle.length > MAX_CHAT_TITLE_LENGTH) {
      throw new Error(`Chat name cannot exceed ${MAX_CHAT_TITLE_LENGTH} characters`);
    }

    const previousTitle = chatHistoryRef.current.find((chat) => chat.guid === chatGuid)?.title;
    const applyTitle = (prev: ChatSession[]) => prev.map((chat) => (
      chat.guid === chatGuid ? { ...chat, title: nextTitle } : chat
    ));
    setChatHistory(applyTitle);
    setChatHistorySearchResults(applyTitle);

    try {
      await backendClient.patch(`/case-partner/${chatGuid}`, { title: nextTitle }, {
        params: { matter_guid: selectedCase },
      });
    } catch (error) {
      const revertTitle = (prev: ChatSession[]) => prev.map((chat) => (
        chat.guid === chatGuid ? { ...chat, title: previousTitle ?? chat.title } : chat
      ));
      setChatHistory(revertTitle);
      setChatHistorySearchResults(revertTitle);
      throw error;
    }
  };

  // Delete a chat
  const deleteChat = async (chatGuid: string) => {
    if (!selectedCase || !chatGuid) return;
    
    // First check if this is the current chat
    const isCurrentChat = currentChatGuid === chatGuid;
    
    // Optimistically update the UI
    setChatHistory(prev => prev.filter(chat => chat.guid !== chatGuid));
    setChatHistorySearchResults((prev) => prev.filter((chat) => chat.guid !== chatGuid));
    
    // If we're deleting the current chat, start a new one immediately
    if (isCurrentChat) {
      startNewChat();
    }
    
    try {
      await backendClient.delete(`/case-partner/${chatGuid}?matter_guid=${selectedCase}`);
    } catch (error) {
      console.error('Error deleting chat:', error);
      
      // If the API call fails, reload the chat history to restore the state
      setTimeout(loadChatHistory, 500);
    }
  };

  return {
    messages,
    chatHistory,
    isLoading,
    isStreaming,
    isStreamingCurrentChat,
    isCurrentChatTurnActive: currentChatTurnActive,
    hasActiveGeneration,
    canAbortStreaming,
    stopRequested,
    streamingChatGuid,
    sendMessage,
    streamingContent,
    streamingQuickReplyQuestions,
    streamingTemplateChoices,
    streamingThinkingSteps,
    streamingTextInsertions,
    streamingWaitMarkers,
    finalAnswerConfirmed,
    streamCatchingUp,
    turnStartedAt,
    turnWorkedDurationMs,
    abortStreaming,
    startNewChat,
    loadChat,
    suppressRouteChatLoadRef,
    loadMoreChatHistory,
    searchChatHistory,
    renameChat,
    deleteChat,
    chatHistoryHasMore,
    isLoadingMoreChatHistory,
    chatHistorySearchQuery,
    chatHistorySearchResults,
    chatHistorySearchHasMore,
    focusMessage,
    requestFocusMessage,
    currentChatGuid,
    isThinking,
    latestProducedDraftDocument,
    clearLatestProducedDraftDocument,
  };
};
